import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import Iconify from '@/components/iconify';
import Label from '@/components/label';
import { TEST_EVENT_OUTCOME } from '@/services/apps';

const RESPONSE_PREVIEW_LIMIT = 500;

const FICTITIOUS_DATA_HINT =
  'O teste envia dados fictícios de comprador e produto. Se o seu sistema procura esses dados, ele pode recusar o teste.';

export const formatTestEventResponse = (response) => {
  if (response === null || response === undefined) {
    return null;
  }

  const text = typeof response === 'string' ? response : JSON.stringify(response, null, 2);

  if (!text.trim()) {
    return null;
  }

  return text.length > RESPONSE_PREVIEW_LIMIT ? `${text.slice(0, RESPONSE_PREVIEW_LIMIT)}…` : text;
};

// O 422 do endpoint de teste cobre dois casos: o destino respondeu fora de 2xx, ou o envio falhou
// antes de haver resposta (conexão, DNS, TLS, timeout), com status_code 400 e o texto da exceção em
// `response`. Por isso a tela mostra o código sem afirmar que o endpoint respondeu.
export const describeTestEventFailure = ({ httpStatus, statusCode, response, detail }) => {
  if (httpStatus === 422) {
    return {
      code: statusCode,
      body: formatTestEventResponse(response) ?? 'Resposta sem corpo',
    };
  }

  if (httpStatus === 400 && detail) {
    return { message: detail };
  }

  return {
    message: httpStatus
      ? `Não foi possível enviar este evento (erro ${httpStatus}). Tente novamente.`
      : 'Não foi possível enviar este evento. Verifique sua conexão e tente novamente.',
  };
};

TestEventResults.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  events: PropTypes.array,
  onViewLogs: PropTypes.func,
};

export default function TestEventResults({ results, events = [], onViewLogs }) {
  const eventNames = new Map(events.map(({ custom_id: customId, name }) => [customId, name]));
  const hasDestinationFailure = results.some(({ httpStatus }) => httpStatus === 422);

  return (
    <Stack gap={1.5} data-testid="test-event-results">
      {results.map((result) => {
        const isSent = result.outcome === TEST_EVENT_OUTCOME.SENT;
        const failure = isSent ? null : describeTestEventFailure(result);

        return (
          <Box
            key={result.eventId}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle2">
                {eventNames.get(result.eventId) || result.eventId}
              </Typography>
              <Label color={isSent ? 'success' : 'error'}>{isSent ? 'Enviado' : 'Falhou'}</Label>
            </Stack>

            {Number.isInteger(failure?.code) && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Código: {failure.code}
              </Typography>
            )}

            {failure?.body && (
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  mt: 1,
                  mb: 0,
                  p: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                {failure.body}
              </Typography>
            )}

            {failure?.message && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {failure.message}
              </Typography>
            )}
          </Box>
        );
      })}

      {hasDestinationFailure && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {FICTITIOUS_DATA_HINT}
        </Typography>
      )}

      {onViewLogs && (
        <Box>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<Iconify icon="foundation:list-number" />}
            onClick={onViewLogs}
          >
            Ver logs
          </Button>
        </Box>
      )}
    </Stack>
  );
}
